import type { IPlayerRegistrationPayload } from '@lucimark/shared/models/player-registration-payload'
import type { TAPIClientService } from './APIClientService'
import type { TAuthenticationService } from './AuthenticationService'
import type { TWebSocketService } from './WebSocketService'

/** Live API result vs IndexedDB offline fallback (preserves snapshot age for diagnostics). */
export type TRegistrationStatusEmissionSource = 'live' | 'cached'

export interface IRegistrationStatusEmission {
  payload: IPlayerRegistrationPayload | null
  source: TRegistrationStatusEmissionSource
  /** Wall time when the IndexedDB snapshot was saved; set when `source === 'cached'`. */
  snapshotSavedAtMs?: number | null
}
import {
  PLAYER_POOLING_PERIOD_MS,
  PLAYER_POOLING_PERIOD_WHILE_UNREGISTERED_MS
} from '@lucimark/shared/constants/player'
import { computeUnregisteredPoolingPeriodMs } from '@lucimark/shared/helpers/unregisteredPoolingPeriod'
import Emittery from 'emittery'
import debounce from 'lodash.debounce'
import {
  PLAYER_AUTH_SOFT_RETRY_CODES,
  recordPlayerAuthAmbiguousFailure,
  registrationBackupDispositionForPersistedClear,
  resetPlayerAuthAmbiguousFailureStreak,
  shouldClearPersistedRegistration,
  tryAcquireAmbiguousClearCooldown
} from '@/helpers/playerRegistrationAuthPolicy'
import { reportRegistrationClearedToSentry } from '@/helpers/reportRegistrationClearedToSentry'
import { createRegistrationClearDirectoryGate } from '@/helpers/tryAbortAmbiguousClearViaDirectory'
import {
  ApiClientServiceError,
  ApiClientServiceUnauthorizedError
} from './APIClientService'
import { WebSocketClientState } from './WebSocketClient'
import { WebSocketServiceEvent } from './WebSocketService'

export enum PoolingServiceEvent {
  RUNNING = 'running',
  REGISTRATION_STATUS = 'registration_status'
}

export interface PoolingServiceEventMap {
  [PoolingServiceEvent.RUNNING]: boolean
  [PoolingServiceEvent.REGISTRATION_STATUS]: IRegistrationStatusEmission
}

export interface IPoolingService {}

export class PoolingService extends Emittery<PoolingServiceEventMap> implements IPoolingService {
  private _poolingPeriodMs = 5000
  private _websocketService: TWebSocketService
  private _apiClientService: TAPIClientService
  private _authenticationService: TAuthenticationService
  private _registrationClearDirectoryGate: ReturnType<typeof createRegistrationClearDirectoryGate>
  private _getOfflineRegistrationStatusSnapshot?: () => Promise<{
    payload: IPlayerRegistrationPayload
    savedAtMs: number
  } | null>
  private _running: boolean = false
  private _timeout: NodeJS.Timeout | null = null
  /** Wall time when the current unregistered streak began (null when registered / unknown). */
  private _unregisteredSinceMs: number | null = null

  constructor(params: {
    websocketService: TWebSocketService
    apiClientService: TAPIClientService
    authenticationService: TAuthenticationService
    /** When registration-status fetch fails (e.g. offline), emit this snapshot so playback can continue. */
    getOfflineRegistrationStatusSnapshot?: () => Promise<{
      payload: IPlayerRegistrationPayload
      savedAtMs: number
    } | null>
  }) {
    super()
    const { websocketService, apiClientService, authenticationService, getOfflineRegistrationStatusSnapshot } = params
    this._websocketService = websocketService
    this._apiClientService = apiClientService
    this._authenticationService = authenticationService
    this._registrationClearDirectoryGate = createRegistrationClearDirectoryGate({
      fetchRegistration: opts => apiClientService.fetchRegistration(opts),
      authenticationService,
      fetchRegistrationStatus: () => apiClientService.fetchRegistrationStatusWithStaleCredentialRecovery()
    })
    this._getOfflineRegistrationStatusSnapshot = getOfflineRegistrationStatusSnapshot

    websocketService.on(WebSocketServiceEvent.STATE_UPDATE, async (state) => {
      await this._setDebouncedRunning(state !== WebSocketClientState.CONNECTED)
    })
    void this._setDebouncedRunning(this._websocketService.webSocketClientState !== WebSocketClientState.CONNECTED)
  }

  get running() {
    return this._running
  }

  /**
   * Reset unregistered poll backoff to the fast 2s window (pairing UX).
   * Call when the operator is actively interacting with the pairing screen.
   */
  resetUnregisteredPollBackoff(): void {
    this._unregisteredSinceMs = Date.now()
    this._poolingPeriodMs = computeUnregisteredPoolingPeriodMs(0)
  }

  /** After tenant revoke via polling, fetch pairing row immediately instead of waiting for the next interval. */
  private async _fetchPairingRegistrationAfterPersistedClear(): Promise<void> {
    try {
      const pairing = await this._apiClientService.fetchRegistration()
      await this._authenticationService.setRegistration(pairing)
    }
    catch {
      // next poll retries
    }
  }

  private _markUnregisteredPolling(): number {
    if (this._unregisteredSinceMs == null) {
      this._unregisteredSinceMs = Date.now()
    }
    const period = computeUnregisteredPoolingPeriodMs(Date.now() - this._unregisteredSinceMs)
    this._poolingPeriodMs = period
    return period
  }

  private _markRegisteredPolling(): void {
    this._unregisteredSinceMs = null
    this._poolingPeriodMs = PLAYER_POOLING_PERIOD_MS
  }

  private _setDebouncedRunning = debounce(async (running: boolean) => {
    const switchedState = this._running !== running
    this._running = running
    if (switchedState) {
      await this.emit(PoolingServiceEvent.RUNNING, running)
      await this.doPooling()
    }
  }, 500)

  async doPooling() {
    let poolingPeriodMs = this._poolingPeriodMs
    if (this._timeout !== null) {
      clearTimeout(this._timeout)
      this._timeout = null
    }
    if (this.running) {
      try {
        let registration = await this._authenticationService.getRegistration()
        if (registration === null) {
          try {
            registration = await this._apiClientService.fetchRegistration()
          }
          catch (e) {
            if (e instanceof ApiClientServiceUnauthorizedError && e.code != null && PLAYER_AUTH_SOFT_RETRY_CODES.has(e.code)) {
              registration = await this._apiClientService.fetchRegistration()
            }
            else {
              throw e
            }
          }
          await this._authenticationService.setRegistration(registration)
          if (registration.tenantId === null) {
            poolingPeriodMs = this._markUnregisteredPolling()
            return
          }
        }
        if (registration.tenantId === null) {
          poolingPeriodMs = this._markUnregisteredPolling()
          return
        }
        try {
          const registrationStatus = await this._apiClientService.fetchRegistrationStatusWithStaleCredentialRecovery()
          await this.emit(PoolingServiceEvent.REGISTRATION_STATUS, { payload: registrationStatus, source: 'live' })
          if (registrationStatus !== null) {
            resetPlayerAuthAmbiguousFailureStreak()
            this._markRegisteredPolling()
            poolingPeriodMs = this._poolingPeriodMs
          }
        }
        catch (err) {
          if (err instanceof ApiClientServiceUnauthorizedError) {
            const status = err.httpStatus ?? 401
            const code = err.code
            const decision = shouldClearPersistedRegistration({ code, status })
            if (decision === 'clear') {
              const revokeGate = await this._registrationClearDirectoryGate('revoke')
              if (revokeGate !== 'proceed_clear') {
                poolingPeriodMs = Math.max(poolingPeriodMs, this._markUnregisteredPolling())
              }
              else {
                reportRegistrationClearedToSentry({
                  reason: 'server_revoke',
                  source: 'pooling',
                  context: 'pooling_registration_status',
                  code,
                  httpStatus: status
                })
                await this._authenticationService.setRegistration(null, {
                  registrationBackup: registrationBackupDispositionForPersistedClear('server_revoke')
                })
                await this.emit(PoolingServiceEvent.REGISTRATION_STATUS, { payload: null, source: 'live' })
                poolingPeriodMs = this._markUnregisteredPolling()
                await this._fetchPairingRegistrationAfterPersistedClear()
              }
            }
            else if (decision === 'ambiguous' && recordPlayerAuthAmbiguousFailure('pooling')) {
              const gate = await this._registrationClearDirectoryGate('ambiguous')
              if (gate !== 'proceed_clear') {
                poolingPeriodMs = Math.max(poolingPeriodMs, this._markUnregisteredPolling())
              }
              else if (!tryAcquireAmbiguousClearCooldown()) {
                // A clear already fired recently; suppress this burst to avoid cascading loss.
              }
              else {
                reportRegistrationClearedToSentry({
                  reason: 'ambiguous_streak',
                  source: 'pooling',
                  context: 'pooling_registration_status',
                  code,
                  httpStatus: status
                })
                await this._authenticationService.setRegistration(null, {
                  registrationBackup: registrationBackupDispositionForPersistedClear('ambiguous_streak')
                })
                await this.emit(PoolingServiceEvent.REGISTRATION_STATUS, { payload: null, source: 'live' })
                resetPlayerAuthAmbiguousFailureStreak()
              }
            }
            const regAfterAuthError = await this._authenticationService.getRegistration()
            poolingPeriodMs = regAfterAuthError?.tenantId == null
              ? this._markUnregisteredPolling()
              : Math.max(poolingPeriodMs, PLAYER_POOLING_PERIOD_WHILE_UNREGISTERED_MS)
          }
          else if (
            err instanceof ApiClientServiceError
            && err.httpStatus === 429
            && err.retryAfterMs != null
            && Number.isFinite(err.retryAfterMs)
          ) {
            poolingPeriodMs = Math.max(poolingPeriodMs, err.retryAfterMs)
          }
          else {
            const snap = await this._getOfflineRegistrationStatusSnapshot?.() ?? null
            if (snap !== null) {
              await this.emit(PoolingServiceEvent.REGISTRATION_STATUS, {
                payload: snap.payload,
                source: 'cached',
                snapshotSavedAtMs: snap.savedAtMs
              })
            }
          }
          // network errors — retry on next poll interval
        }
      }
      catch {
        // fetchRegistration network error — retry on next poll interval
      }
      finally {
        this._timeout = setTimeout(() => {
          void this.doPooling()
        }, poolingPeriodMs)
      }
    }
  }
}

export type TPoolingService = PoolingService
